import type { EnvironmentThreadShell } from "@cleo/client-runtime/state/shell";
import {
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
  type AtomCommandResult,
} from "@cleo/client-runtime/state/runtime";
import { useEffect, useRef } from "react";

import { resolveShortcutCommand } from "~/keybindings";
import { isTerminalFocused } from "~/lib/terminalFocus";
import { appAtomRegistry } from "~/rpc/atomRegistry";
import { readEnvironmentSupportsSettlement } from "~/state/entities";
import { primaryServerKeybindingsAtom } from "~/state/server";
import { threadEnvironment } from "~/state/threads";
import { useAtomCommand } from "~/state/use-atom-command";
import { resolveRoomRailShortcutAction } from "./RoomRailSection.logic";
import { toastManager } from "./ui/toast";

/**
 * UP13 — the Room Rail's two keyboard doors on the ADDRESSED session's bound
 * thread: `thread.settle` settles or restores (upstream #8089), `thread.stop`
 * interrupts the running turn (upstream #4308, unbound by default). The
 * listener runs on the window like the route's own shortcuts and marks the
 * event handled so no second handler acts; it never touches keys while the
 * terminal owns them (the `when` clause and the resolver both refuse).
 *
 * The hook leans only on doors the rail already holds (the thread atoms and
 * the registry), so the rail's own module graph stays what its stories mock.
 */
export function useRoomRailShortcuts(input: {
  readonly activeSessionId: string | null;
  readonly executionBySession: ReadonlyMap<string, EnvironmentThreadShell>;
}): void {
  const settle = useAtomCommand(threadEnvironment.settle, { reportFailure: false });
  const unsettle = useAtomCommand(threadEnvironment.unsettle, { reportFailure: false });
  const interruptTurn = useAtomCommand(threadEnvironment.interruptTurn, { reportFailure: false });
  // The latest inputs ride a ref so the window listener attaches once.
  const latest = useRef({ input, settle, unsettle, interruptTurn });
  latest.current = { input, settle, unsettle, interruptTurn };
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const terminalFocus = isTerminalFocused();
      const keybindings = appAtomRegistry.get(primaryServerKeybindingsAtom);
      const command = resolveShortcutCommand(event, keybindings, {
        context: { terminalFocus, terminalOpen: false, previewFocus: false, previewOpen: false },
      });
      if (command !== "thread.settle" && command !== "thread.stop") return;
      const { input: current } = latest.current;
      const shell =
        current.activeSessionId === null
          ? null
          : (current.executionBySession.get(current.activeSessionId) ?? null);
      const action = resolveRoomRailShortcutAction(command, {
        shell,
        terminalFocus,
        now: new Date().toISOString(),
      });
      if (action === null || shell === null) return;
      // Version skew: a server that predates settlement never sees the command.
      if (action.kind !== "stop" && !readEnvironmentSupportsSettlement(shell.environmentId)) return;
      event.preventDefault();
      event.stopPropagation();
      const environmentId = shell.environmentId;
      const report = async (
        title: string,
        run: () => Promise<AtomCommandResult<unknown, unknown>>,
      ) => {
        const result = await run();
        if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
          const error = squashAtomCommandFailure(result);
          toastManager.add({
            type: "error",
            title,
            description: error instanceof Error ? error.message : "An error occurred.",
          });
        }
      };
      if (action.kind === "settle")
        void report("Failed to settle session", () =>
          latest.current.settle({ environmentId, input: { threadId: shell.id } }),
        );
      else if (action.kind === "restore")
        void report("Failed to restore session", () =>
          latest.current.unsettle({ environmentId, input: { threadId: shell.id, reason: "user" } }),
        );
      else {
        const runningTurnId =
          shell.session?.status === "running" ? shell.session.activeTurnId : null;
        void report("Failed to stop session", () =>
          latest.current.interruptTurn({
            environmentId,
            input: {
              threadId: shell.id,
              ...(runningTurnId !== null ? { turnId: runningTurnId } : {}),
            },
          }),
        );
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
}
